import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, MailCheck, ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
    } catch { /* generic response by design */ }
    setSending(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md p-8 shadow-card" data-testid="forgot-password-card">
        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-password-sent">
            <MailCheck className="h-10 w-10 mx-auto text-emerald-600" />
            <h1 className="text-xl font-semibold">{t('forgotPassword.sentTitle', { defaultValue: 'Check your email' })}</h1>
            <p className="text-sm text-muted-foreground">
              {t('forgotPassword.sentBody', { defaultValue: 'If an account exists for that address, we sent a reset link. It is valid for 60 minutes and can be used once.' })}
            </p>
            <Link to="/login" className="text-sm text-primary underline" data-testid="forgot-back-login">
              {t('forgotPassword.backToLogin', { defaultValue: 'Back to sign in' })}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <h1 className="text-xl font-semibold">{t('forgotPassword.title', { defaultValue: 'Forgot your password?' })}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t('forgotPassword.subtitle', { defaultValue: 'Enter your account email and we will send you a reset link.' })}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fp-email">{t('forgotPassword.email', { defaultValue: 'Email' })}</Label>
              <Input id="fp-email" type="email" required value={email} autoFocus
                     onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email" />
            </div>
            <Button type="submit" className="w-full" disabled={sending} data-testid="forgot-password-submit">
              {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('forgotPassword.submit', { defaultValue: 'Send reset link' })}
            </Button>
            <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('forgotPassword.backToLogin', { defaultValue: 'Back to sign in' })}
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}
